import type {
  DatabaseAiFieldCellRequestAwareness,
  DatabaseAiFieldRunAwareness
} from '@databases/collab-state-manager/y-database-telepointers';
import { snapshot, subscribe } from 'valtio';

import type { AiFieldCellTarget, AiFieldRunTarget } from './ai-field-run-awareness';

type AiFieldActivitySource = {
  aiFieldRuns: Record<number, DatabaseAiFieldRunAwareness>;
  aiFieldCellRequests: Record<number, DatabaseAiFieldCellRequestAwareness>;
};

type AiFieldActivityState = {
  aiFieldRuns: Readonly<Record<number, DatabaseAiFieldRunAwareness>>;
  aiFieldCellRequests: Readonly<Record<number, DatabaseAiFieldCellRequestAwareness>>;
};

type AiFieldCellActivityTarget = AiFieldCellTarget & {
  isTargetValueEmpty: boolean;
};

type ActivityListener = () => void;

const createFieldActivityKey = (
  kind: 'aggregate' | 'all-entries' | 'empty-target-entries',
  { aiFieldType, fieldId }: AiFieldRunTarget
): string => JSON.stringify([kind, aiFieldType, fieldId]);

const createEntryActivityKey = ({
  aiFieldType,
  fieldId,
  entryId
}: AiFieldCellTarget): string => JSON.stringify(['entry', aiFieldType, fieldId, entryId]);

const addRunActivity = (
  activeKeys: Set<string>,
  run: DatabaseAiFieldRunAwareness
): void => {
  const target = { aiFieldType: run.aiFieldType, fieldId: run.fieldId };
  activeKeys.add(createFieldActivityKey('aggregate', target));

  switch (run.scope.kind) {
    case 'entry':
      activeKeys.add(createEntryActivityKey({ ...target, entryId: run.scope.entryId }));
      return;
    case 'entries':
      for (const entryId of run.scope.entryIds) {
        activeKeys.add(createEntryActivityKey({ ...target, entryId }));
      }
      return;
    case 'field':
      activeKeys.add(createFieldActivityKey(run.scope.operation, target));
      return;
    default: {
      const unhandledScope: never = run.scope;
      throw new Error(`Unhandled AI field activity scope: ${unhandledScope}`);
    }
  }
};

const createActiveKeys = ({
  aiFieldRuns,
  aiFieldCellRequests
}: AiFieldActivityState): Set<string> => {
  const activeKeys = new Set<string>();

  for (const run of Object.values(aiFieldRuns)) {
    addRunActivity(activeKeys, run);
  }

  for (const { requests } of Object.values(aiFieldCellRequests)) {
    for (const request of requests) {
      const target = {
        aiFieldType: request.aiFieldType,
        fieldId: request.fieldId
      };
      activeKeys.add(createFieldActivityKey('aggregate', target));
      activeKeys.add(createEntryActivityKey({ ...target, entryId: request.entryId }));
    }
  }

  return activeKeys;
};

/**
 * Organizes shared AI-field activity by field and cell so UI components only
 * subscribe to relevant changes instead of rerendering for every AI-field update.
 */
class AiFieldActivityIndex {
  readonly #source: AiFieldActivitySource | undefined;
  readonly #listenersByKey = new Map<string, Set<ActivityListener>>();
  #sourceUnsubscribes: readonly (() => void)[] | undefined;
  #activeKeys: ReadonlySet<string> = new Set();
  #aiFieldRunsSnapshot: object | undefined;
  #aiFieldCellRequestsSnapshot: object | undefined;

  constructor(source?: AiFieldActivitySource) {
    this.#source = source;
    this.#refresh();
  }

  subscribeToField(target: AiFieldRunTarget, listener: ActivityListener): () => void {
    return this.#subscribe([createFieldActivityKey('aggregate', target)], listener);
  }

  subscribeToCell(
    target: AiFieldCellActivityTarget,
    listener: ActivityListener
  ): () => void {
    const keys = [
      createFieldActivityKey('all-entries', target),
      createEntryActivityKey(target)
    ];
    if (target.isTargetValueEmpty) {
      keys.push(createFieldActivityKey('empty-target-entries', target));
    }

    return this.#subscribe(keys, listener);
  }

  isFieldActive(target: AiFieldRunTarget): boolean {
    return this.#getActiveKeys().has(createFieldActivityKey('aggregate', target));
  }

  isCellActive(target: AiFieldCellActivityTarget): boolean {
    const activeKeys = this.#getActiveKeys();
    return (
      activeKeys.has(createFieldActivityKey('all-entries', target)) ||
      (target.isTargetValueEmpty &&
        activeKeys.has(createFieldActivityKey('empty-target-entries', target))) ||
      activeKeys.has(createEntryActivityKey(target))
    );
  }

  #subscribe(keys: readonly string[], listener: ActivityListener): () => void {
    for (const key of keys) {
      const listeners = this.#listenersByKey.get(key) ?? new Set();
      listeners.add(listener);
      this.#listenersByKey.set(key, listeners);
    }
    this.#startSourceSubscriptions();

    return () => {
      for (const key of keys) {
        const listeners = this.#listenersByKey.get(key);
        listeners?.delete(listener);
        if (listeners?.size === 0) {
          this.#listenersByKey.delete(key);
        }
      }
      if (this.#listenersByKey.size === 0) {
        this.#stopSourceSubscriptions();
      }
    };
  }

  #getActiveKeys(): ReadonlySet<string> {
    this.#refresh();
    return this.#activeKeys;
  }

  #startSourceSubscriptions(): void {
    if (!this.#source || this.#sourceUnsubscribes) {
      return;
    }

    this.#sourceUnsubscribes = [
      subscribe(this.#source.aiFieldRuns, this.#refresh, true),
      subscribe(this.#source.aiFieldCellRequests, this.#refresh, true)
    ];
    this.#refresh();
  }

  #stopSourceSubscriptions(): void {
    for (const unsubscribe of this.#sourceUnsubscribes ?? []) {
      unsubscribe();
    }
    this.#sourceUnsubscribes = undefined;
  }

  #refresh = (): void => {
    if (!this.#source) {
      return;
    }

    const aiFieldRuns = snapshot(this.#source.aiFieldRuns);
    const aiFieldCellRequests = snapshot(this.#source.aiFieldCellRequests);
    if (
      aiFieldRuns === this.#aiFieldRunsSnapshot &&
      aiFieldCellRequests === this.#aiFieldCellRequestsSnapshot
    ) {
      return;
    }

    this.#aiFieldRunsSnapshot = aiFieldRuns;
    this.#aiFieldCellRequestsSnapshot = aiFieldCellRequests;
    const nextActiveKeys = createActiveKeys({ aiFieldRuns, aiFieldCellRequests });
    const listenersToNotify = new Set<ActivityListener>();
    for (const key of new Set([...this.#activeKeys, ...nextActiveKeys])) {
      if (this.#activeKeys.has(key) === nextActiveKeys.has(key)) {
        continue;
      }
      for (const listener of this.#listenersByKey.get(key) ?? []) {
        listenersToNotify.add(listener);
      }
    }

    this.#activeKeys = nextActiveKeys;
    for (const listener of listenersToNotify) {
      listener();
    }
  };
}

const inactiveAiFieldActivityIndex = new AiFieldActivityIndex();
const activityIndexes = new WeakMap<AiFieldActivitySource, AiFieldActivityIndex>();

export const getAiFieldActivityIndex = (
  source: AiFieldActivitySource | undefined
): AiFieldActivityIndex => {
  if (!source) {
    return inactiveAiFieldActivityIndex;
  }

  const existingIndex = activityIndexes.get(source);
  if (existingIndex) {
    return existingIndex;
  }

  const index = new AiFieldActivityIndex(source);
  activityIndexes.set(source, index);
  return index;
};
